import { GridRowId } from '@mui/x-data-grid/models/gridRows'
import { produce } from 'immer'
import {
  AUTO_MATCH_RECEIPTS_SUCCESS,
  AutoMatchReceiptsActions,
} from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.actions'
import { ReceiptAutoMatchPushTaskStatus } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.reducers'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { TransactionListResponse } from 'src/models/transaction.models'
import {
  GET_REPORT_TRANSACTIONS_SUCCESS,
  GET_TRANSACTION_SUCCESS,
  GET_YOUR_REPORT_TRANSACTIONS_SUCCESS,
  GetAsyncTransactionUpdateJobStatusActions,
  GetReportTransactionsSuccessAction,
  GetReportYourTransactionsActions,
  GetTransactionSuccessAction,
  UPDATE_SELECT_ALL_TRANSACTIONS_BOX_VISIBLE,
  UPDATE_SELECT_ALL_TRANSACTIONS_FILTERS,
  UPDATE_SELECTED_TRANSACTIONS,
  UpdateSelectAllTransactionsBoxVisibleActions,
  UpdateSelectAllTransactionsFiltersActions,
  UpdateSelectedTransactionActions,
} from 'src/redux/actions/data/transactions.actions'

export interface TransactionsState {
  listResponses: Record<string, TransactionListResponse>
  /*
    this data DOES NOT live in state.app.transactions. The transactions table, where users can
    perform an action on selected transactions is displayed throughout different parts of the website
    i.e. accounts/cc_##/transaction && transactions/all
  */
  selectedTransactions: Set<GridRowId>
  transactionIdsConfirmingSupplier: string[]
  selectAllTransactionsBoxVisible: boolean
  selectAllTransactionsFilters?: TransactionsFiltersBody
}

export const initialState: TransactionsState = {
  listResponses: {},
  selectedTransactions: new Set(),
  transactionIdsConfirmingSupplier: [],
  selectAllTransactionsBoxVisible: false,
  selectAllTransactionsFilters: undefined,
}

const areSelectionSetsEqual = (
  left: ReadonlySet<GridRowId>,
  right: ReadonlySet<GridRowId>,
): boolean => {
  if (left === right) return true
  if (left.size !== right.size) return false
  for (const id of left) {
    if (!right.has(id)) return false
  }
  return true
}

export type Actions =
  | GetTransactionSuccessAction
  | GetReportTransactionsSuccessAction
  | GetReportYourTransactionsActions
  | UpdateSelectedTransactionActions
  | AutoMatchReceiptsActions
  | UpdateSelectAllTransactionsBoxVisibleActions
  | UpdateSelectAllTransactionsFiltersActions
  | GetAsyncTransactionUpdateJobStatusActions

const TransactionsReducer = (
  state: TransactionsState = initialState,
  action: Actions,
): TransactionsState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case GET_YOUR_REPORT_TRANSACTIONS_SUCCESS:
      case GET_REPORT_TRANSACTIONS_SUCCESS: {
        draft.listResponses = action.payload.report.transactions.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.id]: curr,
          }),
          draft.listResponses,
        )
        return draft
      }

      case AUTO_MATCH_RECEIPTS_SUCCESS: {
        draft.listResponses = action.payload.tasks.reduce((acc, curr) => {
          if (
            curr.transactionId !== undefined &&
            draft.listResponses[curr.transactionId] !== undefined &&
            curr.status === ReceiptAutoMatchPushTaskStatus.SUCCESS
          ) {
            return {
              ...acc,
              [curr.transactionId]: {
                ...acc[curr.transactionId],
                ...{
                  hasAttachments: true,
                  attachmentsCount: curr.attachmentsCount,
                },
              },
            }
          } else {
            return acc
          }
        }, draft.listResponses)
        return draft
      }

      case GET_TRANSACTION_SUCCESS: {
        draft.listResponses = {
          ...draft.listResponses,
          [action.payload.id]: action.payload,
        }
        return draft
      }

      case UPDATE_SELECTED_TRANSACTIONS: {
        const nextSelectedTransactions = action.payload.selectedTransactions
        if (
          !areSelectionSetsEqual(
            draft.selectedTransactions,
            nextSelectedTransactions,
          )
        ) {
          draft.selectedTransactions = new Set(nextSelectedTransactions)
        }
        return draft
      }

      case UPDATE_SELECT_ALL_TRANSACTIONS_BOX_VISIBLE: {
        draft.selectAllTransactionsBoxVisible =
          action.payload.selectAllTransactionsBoxVisible
        return draft
      }

      case UPDATE_SELECT_ALL_TRANSACTIONS_FILTERS: {
        draft.selectAllTransactionsFilters =
          action.payload.selectAllTransactionsFilters
        return draft
      }
    }
  })
}

export default TransactionsReducer
